import { useEffect, useRef, useState } from 'react';
import { stageLabel, stageColor } from '../lib/stages';

export const Card = ({ children, tight, className = '', as: As = 'section', ...rest }) => (
  <As className={`card ${tight ? 'tight' : ''} ${className}`} {...rest}>{children}</As>
);

export const SectionTitle = ({ children, sub }) => (
  <>
    <h2 className="section-title">{children}</h2>
    {sub ? <p className="section-sub">{sub}</p> : null}
  </>
);

export const StageBadge = ({ stage }) => (
  <span className={`badge ${stageColor(stage)}`}>
    <span className="d" aria-hidden="true" />
    {stageLabel(stage)}
  </span>
);

export const Field = ({ label, hint, error, children, id }) => (
  <div className="field">
    <label htmlFor={id}>{label}</label>
    {children}
    {hint && !error ? <span className="sb">{hint}</span> : null}
    {error ? <span className="field-error" role="alert">{error}</span> : null}
  </div>
);

/**
 * Branded select menu. Native desktop option popovers use the operating
 * system's blue highlight and cannot be styled consistently, so this
 * controlled listbox keeps the product palette intact.
 */
export function SelectMenu({ id, value, onChange, options, placeholder = 'Choose an option' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const selected = options.find(option => option.value === value);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const choose = (nextValue) => {
    onChange(nextValue);
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div className="select-wrap" ref={wrapRef}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className="fi select-trigger"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-options`}
        onClick={() => setOpen(current => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span>{selected?.label || placeholder}</span>
        <span className="select-caret" aria-hidden="true">⌄</span>
      </button>
      {open ? (
        <div className="select-menu" id={`${id}-options`} role="listbox" aria-labelledby={id}>
          {options.map(option => (
            <button
              key={option.value}
              type="button"
              className="select-option"
              role="option"
              aria-selected={option.value === value}
              onClick={() => choose(option.value)}
            >
              <span className="select-check" aria-hidden="true">{option.value === value ? '✓' : ''}</span>
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export const Spinner = ({ label = 'Loading…' }) => (
  <div className="loading" role="status">
    <div className="spinner" aria-hidden="true" />
    <span className="sr-only">{label}</span>
  </div>
);

export const Empty = ({ children }) => <p className="empty">{children}</p>;

export const ErrorNote = ({ children, onRetry }) => (
  <div className="note err" role="alert">
    {children}
    {onRetry ? <button className="btn sm" style={{ marginTop: 'var(--s-2)' }} onClick={onRetry}>Try again</button> : null}
  </div>
);

/**
 * Remove button for repeated rows. Carries a real label because "✕" alone
 * tells a screen reader nothing about what is being removed.
 */
export const RemoveButton = ({ onClick, label }) => (
  <button type="button" className="icon-btn" onClick={onClick} aria-label={label}>✕</button>
);
